import React from "react";

export interface TankFuelProperties {
	id?: string;
	fuelType?: string;
	quantity?: string;
	properties?: {
		lcv?: string;
		viscosity?: string;
		grade?: string;
	};
	dateOfLoading?: string;
	portOfLoading?: string;
	bdn?: string;
}

export interface TankRobRow {
	tank_name: { label: string; value: string };
	tankId: string;
	frames?: string;
	totalCapacity?: number;
	rob_as_at_noon?: string;
	tank_in_use?: string;
	fuel_properties?: TankFuelProperties;
}

function formatDate(dateStr?: string) {
	if (!dateStr) return "-";
	const d = new Date(dateStr);
	if (isNaN(d.getTime())) return "-";
	return d.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

function formatTankInUse(value?: string) {
	if (!value) return "-";
	if (value === "in use") return "In Use";
	if (value === "not" || value === "no") return "Not In Use";
	return value;
}

function formatValue(value?: string | number) {
	if (value === undefined || value === null || value === "") return "-";
	return value;
}

function parseSg(sgStr?: string): number | null {
	if (!sgStr) return null;
	const sg = Number(sgStr);
	return isNaN(sg) ? null : sg;
}

// Full capacity in mt = 100% Capacity (m³) × S.G.
function calcFullCapacityMt(
	capacity?: number,
	sgVal?: number | null,
): number | null {
	if (
		capacity === undefined ||
		capacity === null ||
		isNaN(Number(capacity)) ||
		sgVal === null ||
		sgVal === undefined ||
		isNaN(sgVal)
	) {
		return null;
	}
	return Number(capacity) * sgVal;
}

// 90% Capacity (mt) = 100% Capacity (m³) × S.G. × 0.90
function calc90PercentCapacityMt(
	capacity?: number,
	sgVal?: number | null,
): number | null {
	const fullCapacityMt = calcFullCapacityMt(capacity, sgVal);
	if (fullCapacityMt === null) return null;
	return fullCapacityMt * 0.9;
}

function format90PercentCapacityMt(capacity?: number, sgVal?: number | null) {
	const val = calc90PercentCapacityMt(capacity, sgVal);
	return val === null ? "-" : val.toFixed(2);
}

// Filling % = ROB (mt) ÷ (100% Capacity (m³) × S.G.) × 100
function formatFillingPercentageMt(
	robStr?: string,
	capacity?: number,
	sgVal?: number | null,
) {
	if (!robStr || isNaN(Number(robStr))) {
		return "-";
	}

	const fullCapacityMt = calcFullCapacityMt(capacity, sgVal);
	if (fullCapacityMt === null || fullCapacityMt === 0) {
		return "-";
	}

	const robNum = Number(robStr);
	const percentage = (robNum / fullCapacityMt) * 100;
	return `${percentage.toFixed(2)}%`;
}

// Potential Ullage (mt) = 90% Capacity (mt) − ROB (mt)
function calcPotentialUllageMt(
	capacity?: number,
	sgVal?: number | null,
	robStr?: string,
): number | null {
	if (!robStr || isNaN(Number(robStr))) return null;
	const ninetyPercentCapMt = calc90PercentCapacityMt(capacity, sgVal);
	if (ninetyPercentCapMt === null) return null;
	return ninetyPercentCapMt - Number(robStr);
}

// Sums a numeric field across rows, skipping rows where the value is missing/invalid. Returns null (rather than 0) if no row 
function sumNumeric(
	rows: TankRobRow[],
	getValue: (row: TankRobRow) => number | null,
): number | null {
	let sum = 0;
	let hasValue = false;
	for (const row of rows) {
		const val = getValue(row);
		if (val !== null && val !== undefined && !isNaN(val)) {
			sum += val;
			hasValue = true;
		}
	}
	return hasValue ? sum : null;
}

function parseRobValue(robStr?: string): number | null {
	if (!robStr || isNaN(Number(robStr))) return null;
	return Number(robStr);
}

interface TankRobsTotals {
	totalCapacity: number | null;
	total90PercentCapacityMt: number | null;
	totalRob: number | null;
	totalFillingPercent: number | null;
	totalUllage: number | null;
}


function calculateTotals(rows: TankRobRow[], sg: number | null): TankRobsTotals {
	const totalCapacity = sumNumeric(rows, (r) =>
		r.totalCapacity === undefined || r.totalCapacity === null
			? null
			: Number(r.totalCapacity),
	);
	const totalFullCapacityMt = sumNumeric(rows, (r) =>
		calcFullCapacityMt(r.totalCapacity, sg),
	);
	const total90PercentCapacityMt =
		totalFullCapacityMt === null ? null : totalFullCapacityMt * 0.9;
	const totalRob = sumNumeric(rows, (r) => parseRobValue(r.rob_as_at_noon));
	const totalFillingPercent =
		totalFullCapacityMt !== null &&
		totalFullCapacityMt !== 0 &&
		totalRob !== null
			? (totalRob / totalFullCapacityMt) * 100
			: null;
	const totalUllage =
		total90PercentCapacityMt !== null && totalRob !== null
			? total90PercentCapacityMt - totalRob
			: null;

	return {
		totalCapacity,
		total90PercentCapacityMt,
		totalRob,
		totalFillingPercent,
		totalUllage,
	};
}

interface TankRobsTableProps {
	title: string;
	rows?: TankRobRow[];
	sgValue?: string;
}

function TankRobsTable({ title, rows, sgValue }: TankRobsTableProps) {
	if (!rows || rows.length === 0) return null;
	const sg = parseSg(sgValue);
	const totals = calculateTotals(rows, sg);

	return (
		<div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden mt-4">
			<div className="px-4 py-3 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
				<h3 className="text-base font-medium text-gray-800">{title}</h3>
				{sgValue ? (
					<span className="text-xs text-gray-600">
						S.G.: <span className="font-medium text-gray-800">{sgValue}</span>
					</span>
				) : null}
			</div>

			<div className="overflow-x-auto ws-scroll">
				<table className="min-w-full text-sm text-left border-separate border-spacing-0 table-fixed ws-scroll">
					<thead>
						<tr className="bg-gray-100 text-gray-600 uppercase text-xs">
							<th
								className="sticky left-0 z-30 bg-gray-100 px-4 py-2 border-b border-r border-gray-300 truncate max-w-[190px] min-w-[190px]"
								title="Tank Name"
							>
								Tank Name
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[120px]">
								Frames
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[120px]">
								Capacity m³
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[180px]">
								90% Capacity(in mt)
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[130px]">
								ROB as at Noon
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[100px]">
								Filling(%)
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[110px]">
								Tank in Use
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[180px]">
								Potential Ullage (mt)
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[110px]">
								Fuel Type
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[130px]">
								Date of Loading
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[180px]">
								Port of Loading
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[90px]">
								LCV
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[100px]">
								Viscosity
							</th>
							<th className="px-4 py-2 border-b border-gray-200 truncate max-w-[90px]">
								Grade
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const fp = row.fuel_properties;
							const capacity = row.totalCapacity;
							const rob = row.rob_as_at_noon;

							const ninetyPercentCapMtStr = format90PercentCapacityMt(
								capacity,
								sg,
							);
							const fillPercent = formatFillingPercentageMt(rob, capacity, sg);
							const tankName = row.tank_name?.label ?? "-";

							const potentialUllageMtRaw = calcPotentialUllageMt(
								capacity,
								sg,
								rob,
							);
							const potentialUllageMtStr =
								potentialUllageMtRaw === null
									? "-"
									: potentialUllageMtRaw.toFixed(2);

							return (
								<tr
									key={row.tankId}
									className="border-b border-gray-100 group hover:bg-gray-100"
								>
									<td
										className="sticky left-0 z-20 bg-white group-hover:bg-gray-100 px-4 py-2 text-gray-800 font-medium border-r border-gray-300 truncate max-w-[190px] min-w-[190px]"
										title={tankName}
									>
										{tankName}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[120px]"
										title={String(formatValue(row.frames))}
									>
										{formatValue(row.frames)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[120px]"
										title={String(formatValue(capacity))}
									>
										{formatValue(capacity)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[180px]"
										title={String(ninetyPercentCapMtStr)}
									>
										{ninetyPercentCapMtStr}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[130px]"
										title={String(formatValue(rob))}
									>
										{formatValue(rob)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[100px]"
										title={String(fillPercent)}
									>
										{fillPercent}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[110px]"
										title={String(formatTankInUse(row.tank_in_use))}
									>
										{formatTankInUse(row.tank_in_use)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[180px]"
										title={String(potentialUllageMtStr)}
									>
										{potentialUllageMtStr}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[110px]"
										title={String(formatValue(fp?.fuelType))}
									>
										{formatValue(fp?.fuelType)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[130px]"
										title={String(formatDate(fp?.dateOfLoading))}
									>
										{formatDate(fp?.dateOfLoading)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[180px]"
										title={String(formatValue(fp?.portOfLoading))}
									>
										{formatValue(fp?.portOfLoading)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[90px]"
										title={String(formatValue(fp?.properties?.lcv))}
									>
										{formatValue(fp?.properties?.lcv)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[100px]"
										title={String(formatValue(fp?.properties?.viscosity))}
									>
										{formatValue(fp?.properties?.viscosity)}
									</td>
									<td
										className="px-4 py-2 text-gray-700 truncate max-w-[90px]"
										title={String(formatValue(fp?.properties?.grade))}
									>
										{formatValue(fp?.properties?.grade)}
									</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr className="bg-gray-100">
							<td
								className="sticky font-semibold left-0 z-30 bg-gray-100 px-4 py-2 text-gray-800 border-t border-r border-gray-300 truncate max-w-[190px] min-w-[190px]"
								title="Total"
							>
								Total
							</td>
							{/* Frames — left blank */}
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[120px]" />
							<td
								className="px-4 py-2 text-gray-800 border-t border-gray-300 truncate max-w-[120px]"
								title={
									totals.totalCapacity === null
										? "-"
										: totals.totalCapacity.toFixed(2)
								}
							>
								{totals.totalCapacity === null
									? "-"
									: totals.totalCapacity.toFixed(2)}
							</td>
							<td
								className="px-4 py-2 text-gray-800 border-t border-gray-300 truncate max-w-[180px]"
								title={
									totals.total90PercentCapacityMt === null
										? "-"
										: totals.total90PercentCapacityMt.toFixed(2)
								}
							>
								{totals.total90PercentCapacityMt === null
									? "-"
									: totals.total90PercentCapacityMt.toFixed(2)}
							</td>
							<td
								className="px-4 py-2 text-gray-800 border-t border-gray-300 truncate max-w-[130px]"
								title={
									totals.totalRob === null ? "-" : totals.totalRob.toFixed(2)
								}
							>
								{totals.totalRob === null ? "-" : totals.totalRob.toFixed(2)}
							</td>
							<td
								className="px-4 py-2 text-gray-800 border-t border-gray-300 truncate max-w-[100px]"
								title={
									totals.totalFillingPercent === null
										? "-"
										: `${totals.totalFillingPercent.toFixed(2)}%`
								}
							>
								{totals.totalFillingPercent === null
									? "-"
									: `${totals.totalFillingPercent.toFixed(2)}%`}
							</td>
							{/* Tank in Use — left blank */}
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[110px]" />
							<td
								className="px-4 py-2 text-gray-800 border-t border-gray-300 truncate max-w-[180px]"
								title={
									totals.totalUllage === null
										? "-"
										: totals.totalUllage.toFixed(2)
								}
							>
								{totals.totalUllage === null
									? "-"
									: totals.totalUllage.toFixed(2)}
							</td>
							{/* Fuel Type, Date of Loading, Port of Loading, LCV, Viscosity, Grade — left blank */}
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[110px]" />
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[130px]" />
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[180px]" />
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[90px]" />
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[100px]" />
							<td className="px-4 py-2 border-t border-gray-300 truncate max-w-[90px]" />
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
}

export default TankRobsTable;